/**
 * LeadTemplates — per-lead WhatsApp composer.
 *
 * Fuente principal: message_templates backend (canal whatsapp) vía
 * useMessageTemplates. Preselecciona la default de la fase del lead
 * (normalizeStatus(lead.status)) y permite cambiar manualmente a
 * cualquier otra plantilla activa. El textarea edita el mensaje del envío
 * actual — nunca modifica la plantilla compartida ni cambia lead.status.
 *
 * Fallback: si el backend cae, devuelve 200 vacío, o no hay plantillas
 * activas de whatsapp, cae a leadPlaybook.TEMPLATES_BY_STAGE con banner
 * amber. El envío (wa.me + POST /leads/:id/messages) sigue funcionando en
 * ambos modos; el template_key persiste el template_id backend (modo
 * principal) o la key legacy del playbook (modo fallback).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Copy, WhatsappLogo, PaperPlaneTilt, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { normalizeStatus, statusLabel } from "@/lib/leadStatuses";
import { TEMPLATES_BY_STAGE, buildWhatsappUrl as buildLegacyUrl } from "@/lib/leadPlaybook";
import { buildWhatsappUrl, renderWhatsappTemplate, getLeadPhone } from "@/lib/bulkWhatsapp";
import { useMessageTemplates } from "@/hooks/useMessageTemplates";
import {
  pickDefaultTemplateForStage,
  orderTemplatesForStage,
} from "@/lib/messageTemplatesApi";

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Fallback view for a single legacy template row from leadPlaybook. Kept
// in-file so the primary composer stays tight when backend is online.
function LegacyTemplateCard({ tpl, lead, stage, wasSent, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const body = tpl.body(lead || {});
  const phone = lead?.phone || "";
  const hasPhone = !!String(phone).replace(/\D+/g, "");

  const onCopy = async () => {
    const ok = await copyText(body);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    else setError("No se pudo copiar al portapapeles.");
  };
  const onWa = () => {
    const url = buildLegacyUrl(phone, body);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const onMarkSent = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/messages`, {
        template_key: tpl.key,
        channel: tpl.channel || "whatsapp",
        stage,
        preview: body.slice(0, 120),
        linked_checklist_key: tpl.linkedChecklist || null,
        event_type: "sent_confirmed",
      });
      onChanged?.(data);
      setSent(true); setTimeout(() => setSent(false), 1500);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded border border-stone-200 bg-white p-2.5 space-y-2" data-testid={`lead-template-${tpl.key}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-stone-700">{tpl.label}</span>
        {wasSent && (
          <span className="text-[10px] uppercase tracking-widest text-emerald-700 inline-flex items-center gap-0.5">
            <CheckCircle size={10} weight="fill" /> Enviado
          </span>
        )}
      </div>
      <div className="text-sm text-stone-700 whitespace-pre-wrap bg-stone-50 border border-stone-100 rounded p-2">
        {body}
      </div>
      {error && <div className="text-xs text-red-700">{error}</div>}
      <div className="flex flex-wrap gap-1.5 justify-end">
        <button onClick={onCopy} className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1"
                data-testid={`lead-template-${tpl.key}-copy`}>
          <Copy size={12} /> {copied ? "Copiado" : "Copiar"}
        </button>
        <button onClick={onWa} disabled={!hasPhone}
                className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`lead-template-${tpl.key}-wa`}>
          <WhatsappLogo size={12} /> Abrir WhatsApp
        </button>
        <button onClick={onMarkSent} disabled={busy}
                className="text-xs px-2.5 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`lead-template-${tpl.key}-sent`}>
          <PaperPlaneTilt size={12} /> {sent ? "Marcado" : "Marcar enviado"}
        </button>
      </div>
    </div>
  );
}

export default function LeadTemplates({ lead, onChanged, preselectedTemplateId = null }) {
  const stage = normalizeStatus(lead?.status) || "new";
  const { templates, loading, error: backendError, backendEmpty, fallbackUsed, reload } =
    useMessageTemplates({ channel: "whatsapp" });

  // Selected template + edit-in-flight body. Both live in state so the
  // operator can freely tweak the message before firing wa.me / mark-sent.
  const [selectedId, setSelectedId] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState("");
  const previousStageRef = useRef(stage);
  // Track lead identity across renders so we can drop a draft that
  // belonged to a different lead when the operator navigates between
  // leads without unmounting the composer (e.g. LeadSidePanel switching
  // between kanban cards). Ref-only so mutating it never triggers a
  // re-render.
  const previousLeadIdRef = useRef(lead?.lead_id);

  // `preselectedTemplateId` (from LeadDetailPage's ?compose_template=…) is
  // applied at most once, and only when the requested template is present
  // and active. On mount the settled ref is null; after the first attempt
  // it flips regardless of success so the stage-default / drift effect
  // below is unblocked. `preselectionAppliedRef` is a synchronous marker
  // set true in the same commit pass as the preselection setState, used
  // by the stage-default effect to detect the same-batch race window
  // (setSelectedId enqueued but not yet committed) and bail out — otherwise
  // that effect's closure reads the stale `selectedId=null` and clobbers
  // the preselection with the phase default. `preselectionApplied` state
  // feeds the data-* attrs on the root div so tests can assert the
  // preselection landed without scraping the select value.
  const preselectionSettledRef = useRef(!preselectedTemplateId);
  const preselectionAppliedRef = useRef(false);
  const [preselectionApplied, setPreselectionApplied] = useState(false);

  // ------------------------------------------------------------------
  // Backend-body sync state
  // ------------------------------------------------------------------
  // messageEdited (state + ref) — true once the operator types in the
  // textarea. State drives UI (Restaurar mensaje visibility, conflict
  // notice); ref mirrors it so the sync effect can read the freshest value
  // without depending on it and re-firing on every keystroke.
  //
  // loadedBaseBodyRef — snapshot of the backend body we last hydrated into
  // `message`. Used purely to detect drift against a fresh refetch; a ref
  // (not state) so we never trip re-render loops.
  //
  // baseChangedWhileEditing — banner state for the conflict case where
  // admin edited the shared template while the operator had a draft in
  // flight. Silent until the operator has actually typed AND the backend
  // body differs from `loadedBaseBodyRef`.
  const [messageEdited, setMessageEdited] = useState(false);
  const messageEditedRef = useRef(false);
  const setMessageEditedBoth = (v) => { messageEditedRef.current = v; setMessageEdited(v); };
  const [baseChangedWhileEditing, setBaseChangedWhileEditing] = useState(false);
  const loadedBaseBodyRef = useRef("");

  // Preselection effect — runs once when backend templates arrive. The
  // stage-default / drift effect below stays parked (via
  // `preselectionSettledRef`) until this one settles so the operator never
  // sees the phase default flash before the requested template lands.
  // Seeds `loadedBaseBodyRef` from the picked body so a subsequent
  // Recargar doesn't misread the preselected body as an admin edit.
  useEffect(() => {
    if (preselectionSettledRef.current) return;
    if (!preselectedTemplateId) return;
    if (fallbackUsed) {
      // Fallback path has no shared template_id semantics; give up and let
      // the stage-default effect handle selection normally.
      preselectionSettledRef.current = true;
      return;
    }
    if (templates.length === 0) return;
    const tpl = templates.find(
      (t) => t.template_id === preselectedTemplateId && t.is_active,
    );
    preselectionSettledRef.current = true;
    if (!tpl) return; // silent fallback → stage-default effect will handle it
    // Synchronous marker BEFORE any setState — the stage-default effect
    // will fire in the same commit pass and needs to see this flip to know
    // a preselection setter is pending and bail out.
    preselectionAppliedRef.current = true;
    setSelectedId(tpl.template_id);
    setMessage(tpl.body || "");
    loadedBaseBodyRef.current = tpl.body || "";
    setMessageEditedBoth(false);
    setBaseChangedWhileEditing(false);
    setPreselectionApplied(true);
    previousStageRef.current = stage;
    previousLeadIdRef.current = lead?.lead_id;
  }, [preselectedTemplateId, templates, fallbackUsed, stage, lead?.lead_id]);

  // Whenever backend templates arrive or the stage changes, recompute the
  // preselection so the composer stays aligned with the lead's current
  // pipeline stage. Handles three flavours of update:
  //   Case A — no valid selection OR stage moved → pick a default and
  //            hydrate `message` fresh; drop any draft flags.
  //   Case B — selection valid, stage unchanged, backend body drifted:
  //            silently refresh `message` when the operator hasn't typed,
  //            otherwise keep the draft and raise `baseChangedWhileEditing`
  //            so the UI can offer a resolution.
  //   Case B (no drift) → no-op, so keystrokes never cause an update loop.
  // Blocked while the compose preselection is still pending so the
  // stage default can't overwrite the requested template on the same
  // render pass. Deps intentionally exclude messageEdited / message /
  // loadedBaseBody — reading them here would either re-trigger on every
  // keystroke (loop) or read stale values. The ref pair above resolves
  // both concerns.
  useEffect(() => {
    if (!preselectionSettledRef.current) return;
    if (fallbackUsed) {
      setSelectedId(null);
      setMessage("");
      setMessageEditedBoth(false);
      setBaseChangedWhileEditing(false);
      loadedBaseBodyRef.current = "";
      return;
    }
    if (templates.length === 0) return;
    // Same-batch race guard. When templates first arrive with a valid
    // preselection, the effect above flips `preselectionAppliedRef` and
    // enqueues setSelectedId(tpl.template_id) — but this effect's closure
    // still holds the render-time `selectedId=null`, so `templates.find`
    // would return undefined, Case A would fire and clobber the pending
    // preselection (last-write-wins on batched non-updater setState).
    // Bailing on this pass is safe: the next render will have the correct
    // selectedId, `current` will be truthy, and Case A won't fire.
    if (preselectionAppliedRef.current && !selectedId) return;
    const stageChanged = previousStageRef.current !== stage;
    previousStageRef.current = stage;
    const leadChanged = previousLeadIdRef.current !== lead?.lead_id;
    previousLeadIdRef.current = lead?.lead_id;
    const current = templates.find((t) => t.template_id === selectedId);

    // Case A — no valid current selection, or the composer is now focused
    // on a different lead / stage. A draft from lead A must NOT leak into
    // lead B: repick the default and hydrate a fresh body every time
    // identity changes.
    if (!current || stageChanged || leadChanged) {
      const pick = pickDefaultTemplateForStage(templates, stage);
      if (pick) {
        setSelectedId(pick.template_id);
        setMessage(pick.body || "");
        loadedBaseBodyRef.current = pick.body || "";
      } else {
        setSelectedId(null);
        setMessage("");
        loadedBaseBodyRef.current = "";
      }
      setMessageEditedBoth(false);
      setBaseChangedWhileEditing(false);
      return;
    }

    // Case B — selection still valid, same stage. Compare backend body
    // against the last hydration baseline via ref (avoids a state dep).
    const backendBody = current.body || "";
    if (backendBody === loadedBaseBodyRef.current) return;
    if (!messageEditedRef.current) {
      // Silent sync: no draft to protect.
      setMessage(backendBody);
      loadedBaseBodyRef.current = backendBody;
      setBaseChangedWhileEditing(false);
      return;
    }
    // Draft exists — keep it, raise the notice, but advance the baseline so
    // further edits by admin trigger the notice again instead of spamming
    // on the same drift.
    loadedBaseBodyRef.current = backendBody;
    setBaseChangedWhileEditing(true);
  }, [templates, stage, fallbackUsed, selectedId, lead?.lead_id]);

  const orderedTemplates = useMemo(
    () => orderTemplatesForStage(templates, stage),
    [templates, stage],
  );
  const selectedTemplate = useMemo(
    () => templates.find((t) => t.template_id === selectedId) || null,
    [templates, selectedId],
  );
  const isRecommended = !!(selectedTemplate
    && selectedTemplate.stage === stage
    && selectedTemplate.is_default_for_stage);

  const phone = lead?.phone || getLeadPhone(lead);
  const hasPhone = !!String(phone || "").replace(/\D+/g, "");
  const rendered = useMemo(
    () => renderWhatsappTemplate(message, lead || {}),
    [message, lead],
  );

  const handleChangeTemplate = (nextId) => {
    const tpl = templates.find((t) => t.template_id === nextId);
    if (!tpl) return;
    setSelectedId(tpl.template_id);
    setMessage(tpl.body || "");
    loadedBaseBodyRef.current = tpl.body || "";
    setMessageEditedBoth(false);
    setBaseChangedWhileEditing(false);
    setSendError("");
  };

  const handleRestoreBody = () => {
    if (!selectedTemplate) return;
    setMessage(selectedTemplate.body || "");
    loadedBaseBodyRef.current = selectedTemplate.body || "";
    setMessageEditedBoth(false);
    setBaseChangedWhileEditing(false);
  };

  // Adopt the fresh backend body when the admin edited the template while
  // the operator was drafting. Clears the draft flag and dismisses the
  // conflict notice.
  const handleAdoptNewBase = () => {
    if (!selectedTemplate) return;
    setMessage(selectedTemplate.body || "");
    loadedBaseBodyRef.current = selectedTemplate.body || "";
    setMessageEditedBoth(false);
    setBaseChangedWhileEditing(false);
  };

  // Keep the operator's draft as-is. Leaves messageEdited=true so
  // "Restaurar mensaje" stays available; hides the conflict notice.
  const handleKeepDraft = () => {
    setBaseChangedWhileEditing(false);
  };

  // Manual refetch — fires the shared hook's reload so the operator can
  // pull admin's latest edits without closing the composer. Does not
  // touch lead.status, selection or draft (the sync effect above decides
  // how to reconcile the fresh templates).
  const handleReloadTemplates = () => {
    setSendError("");
    reload();
  };

  const handleCopy = async () => {
    const ok = await copyText(rendered);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
    else setSendError("No se pudo copiar al portapapeles.");
  };

  const handleOpenWhatsapp = () => {
    if (!hasPhone) return;
    const url = buildWhatsappUrl(phone, rendered);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleMarkSent = async () => {
    if (!selectedTemplate || !lead?.lead_id) return;
    setBusy(true); setSendError("");
    try {
      // sent_confirmed → backend advances last_message_* and schedules the
      // next follow-up. The composer stays open and reflects the returned
      // lead so `onChanged(data)` can refresh other panels with the fresh
      // next_action_* fields.
      const { data } = await api.post(`/leads/${lead.lead_id}/messages`, {
        template_key: selectedTemplate.template_id,
        channel: "whatsapp",
        stage,
        preview: rendered.slice(0, 120),
        linked_checklist_key: selectedTemplate.linked_checklist_key || null,
        event_type: "sent_confirmed",
      });
      onChanged?.(data);
      setSent(true); setTimeout(() => setSent(false), 1500);
    } catch (e) {
      setSendError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  if (loading) {
    return <div className="text-xs text-stone-400 italic" data-testid="lead-templates-loading">Cargando plantillas…</div>;
  }

  // Fallback path — kept close to the pre-PR2 UX so operators recognise
  // it. Every card here is read-only w.r.t. the shared catalog.
  if (fallbackUsed) {
    const legacy = TEMPLATES_BY_STAGE[stage] || [];
    const sentForStage = new Set(
      (lead?.messages_log || [])
        .filter((m) => m.stage === stage)
        .map((m) => m.template_key),
    );
    const bannerText = backendEmpty
      ? "No hay plantillas compartidas activas. Se muestran versiones locales. Un administrador debe inicializar el catálogo desde Admin → Plantillas de mensajes."
      : "No se pudieron cargar las plantillas del servidor. Se muestran versiones locales; los cambios compartidos no estarán disponibles.";
    return (
      <div className="space-y-2.5">
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
             data-testid="lead-templates-fallback-banner">
          <div className="flex-1">{bannerText}</div>
          <button type="button" onClick={reload}
                  className="text-[10px] uppercase tracking-widest text-amber-900 underline hover:no-underline">
            Reintentar
          </button>
        </div>
        {legacy.length === 0 ? (
          <div className="text-xs text-stone-400 italic">No hay plantillas para esta etapa.</div>
        ) : legacy.map((tpl) => (
          <LegacyTemplateCard
            key={tpl.key}
            tpl={tpl}
            lead={lead}
            stage={stage}
            wasSent={sentForStage.has(tpl.key)}
            onChanged={onChanged}
          />
        ))}
      </div>
    );
  }

  if (!selectedTemplate) {
    return <div className="text-xs text-stone-400 italic" data-testid="lead-templates-empty">No hay plantillas activas para esta etapa.</div>;
  }

  return (
    <div
      className="space-y-2.5"
      data-testid="lead-templates"
      data-preselection-applied={preselectionApplied ? "true" : "false"}
      data-preselected-template-id={preselectionApplied ? selectedId : undefined}
    >
      <label className="block">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-stone-500">Plantilla</span>
          <div className="flex items-center gap-2">
            {isRecommended && (
              <span className="text-[10px] uppercase tracking-widest text-emerald-700"
                    data-testid="lead-templates-recommended">
                Recomendada para esta fase
              </span>
            )}
            <button type="button" onClick={handleReloadTemplates} disabled={loading}
                    className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-900 inline-flex items-center gap-1 disabled:opacity-40"
                    data-testid="lead-templates-reload"
                    title="Vuelve a leer las plantillas del backend por si Admin las editó">
              <ArrowClockwise size={11} /> Recargar
            </button>
          </div>
        </div>
        <select
          className="dv-input w-full text-sm"
          value={selectedId || ""}
          onChange={(e) => handleChangeTemplate(e.target.value)}
          data-testid="lead-templates-select"
        >
          {orderedTemplates.map((t) => {
            const isStageMatch = t.stage === stage;
            const suffix = t.is_default_for_stage && isStageMatch ? " · Recomendada"
              : t.stage == null ? " · Global"
              : isStageMatch ? "" : ` · ${statusLabel(t.stage) || t.stage}`;
            return (
              <option key={t.template_id} value={t.template_id}>{t.name}{suffix}</option>
            );
          })}
        </select>
      </label>

      {baseChangedWhileEditing && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-2"
             data-testid="lead-templates-base-changed">
          <div>La plantilla compartida ha cambiado mientras editabas. Tu mensaje no se ha reemplazado.</div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handleAdoptNewBase}
                    className="text-[11px] px-2 py-1 rounded border border-amber-300 text-amber-900 hover:border-amber-500"
                    data-testid="lead-templates-use-new-base">
              Usar nueva versión
            </button>
            <button type="button" onClick={handleKeepDraft}
                    className="text-[11px] px-2 py-1 rounded border border-amber-300 text-amber-900 hover:border-amber-500"
                    data-testid="lead-templates-keep-draft">
              Mantener mi mensaje
            </button>
          </div>
        </div>
      )}

      <label className="block">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
          Mensaje para este envío
        </div>
        <textarea
          className="dv-input w-full font-mono text-sm"
          rows={6}
          value={message}
          onChange={(e) => { setMessage(e.target.value); setMessageEditedBoth(true); }}
          data-testid="lead-templates-editor"
        />
        <div className="text-[11px] text-stone-500 mt-1">
          Los cambios realizados aquí solo afectan a este envío. Para editar la plantilla compartida,
          utiliza <span className="font-medium">Admin → Plantillas de mensajes</span>.
        </div>
      </label>

      <div className="text-[10px] uppercase tracking-widest text-stone-500">Vista previa</div>
      <div className="text-sm text-stone-700 whitespace-pre-wrap bg-stone-50 border border-stone-100 rounded p-2"
           data-testid="lead-templates-preview">
        {rendered}
      </div>

      {sendError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2">{sendError}</div>
      )}

      <div className="flex flex-wrap gap-1.5 justify-end">
        {message !== (selectedTemplate.body || "") && (
          <button type="button" onClick={handleRestoreBody}
                  className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1"
                  data-testid="lead-templates-restore">
            Restaurar mensaje
          </button>
        )}
        <button type="button" onClick={handleCopy}
                className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1"
                data-testid="lead-templates-copy">
          <Copy size={12} /> {copied ? "Copiado" : "Copiar mensaje"}
        </button>
        <button type="button" onClick={handleOpenWhatsapp} disabled={!hasPhone}
                title={hasPhone ? "" : "Sin teléfono"}
                className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:border-stone-900 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="lead-templates-wa">
          <WhatsappLogo size={12} /> Abrir WhatsApp
        </button>
        <button type="button" onClick={handleMarkSent} disabled={busy}
                className="text-xs px-2.5 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="lead-templates-sent">
          <PaperPlaneTilt size={12} /> {sent ? "Marcado" : "Marcar enviado"}
        </button>
      </div>
    </div>
  );
}
